import React from 'react';
import { FormattedMessage } from 'react-intl';
import { push, replace } from 'react-router-redux';
import { call, put, select } from 'redux-saga/effects';

import * as SpecialOffersActions from '../actions';
import IneligibleOfferModalButton from '../components/IneligibleOfferModalButton/IneligibleOfferModalButton';
import * as SpecialOffersConstants from '../constants';
import * as D from '../deps';
import * as SpecialOffersSelectors from '../selectors';
import * as SpecialOffersServices from '../services';
import * as SpecialOffersUtils from '../utils';

const log = D.Logger.child({ component: 'src/domains/payments/specialOffers/sagas/helpers' }, true);

export function* anonymousSpecialOfferRedemption(specialOfferCode) {
  if (specialOfferCode) {
    // anonymous listener, code in URL but not submitted yet,
    // so submit code anonymously
    yield put(
      SpecialOffersActions.updateSpecialOfferRedemptionState({
        specialOfferCode,
        specialOfferStatus: SpecialOffersConstants.RedemptionStatus.SUBMITTED_ANONYMOUS,
      }),
    );
    try {
      yield call(SpecialOffersServices.redeemCode, { specialOfferCode });
      // if success, continue here
      const specialOfferStatus = yield select(SpecialOffersSelectors.getSpecialOfferStatus);
      if (specialOfferStatus === SpecialOffersConstants.RedemptionStatus.SUBMITTED_ANONYMOUS) {
        // we've submitted the special offer anonymously and the API should have returned data for us to route
        yield put(
          SpecialOffersActions.updateSpecialOfferRedemptionState({
            specialOfferStatus: SpecialOffersConstants.RedemptionStatus.WAITING,
          }),
        );

        // after login, route back to this routine for next step
        yield put(
          D.Actions.Login.setAfterLoginRedirect(
            D.Utils.Route.getCodeRedemptionFlowPath(specialOfferCode),
          ),
        );
        const authLandingPage = yield select(SpecialOffersSelectors.getRedemptionAuthEntry);
        if (authLandingPage === SpecialOffersConstants.AUTH_LANDING_PAGE_REGISTER) {
          yield put(replace(D.Utils.Route.getRegisterPath()));
        } else {
          yield put(replace(D.Utils.Route.getLoginPath()));
        }
        return;
      }
    } catch (err) {
      yield call(specialOfferRedemptionFailure);
    }
    return;
  }

  // anonymous listener with no code in offer URL, reset state, reroute to login, then blank redeem page
  yield put(SpecialOffersActions.resetSpecialOffer());
  yield put(D.Actions.Login.setAfterLoginRedirect(D.Utils.Route.getRedeemPath()));
  yield put(replace(D.Utils.Route.getLoginPath()));
}

export function* loggedInSpecialOfferRedemption(specialOfferCode) {
  if (specialOfferCode) {
    try {
      yield call(SpecialOffersServices.redeemCode, { specialOfferCode });

      const isSpecialOfferPaymentBacked = yield select(
        SpecialOffersSelectors.isSpecialOfferPaymentBacked,
      );
      if (isSpecialOfferPaymentBacked) {
        yield call(paymentBackedSpecialOfferRedemptionSuccess, specialOfferCode);
        return;
      }

      yield call(nonPaymentBackedSpecialOfferRedemptionSuccess, specialOfferCode);
      return;
    } catch (err) {
      yield call(specialOfferRedemptionFailure);
      return;
    }
  }

  // if no special offer code when logged in, go to redeem page
  yield put(replace(D.Utils.Route.getRedeemPath()));
}

export function* paymentBackedSpecialOfferRedemptionSuccess() {
  const getSpecialOfferTier = yield select(SpecialOffersSelectors.getSpecialOfferTier);
  const tierSlug = D.PaymentsUtils.getTierSlugFromTier(getSpecialOfferTier);

  // to support discount plan FMOs we will have to add discountType (morphed into its verificationType) as the second parameter here.
  const redirectPath = D.Utils.Route.getPaymentPath(tierSlug, null, null);

  if (redirectPath) {
    yield put(replace(redirectPath));
    yield call(
      D.PaymentsHelpers.logUpgradePageStatsEvent,
      SpecialOffersConstants.SpecialOfferStatsActions.OFFER_REDEEM_SUCCESS,
    );
    return;
  }

  // else, redirect path missing - should not be reachable, go to redeem as fail-safe
  log.error('payment path missing for special offer tier: ' + getSpecialOfferTier + '/' + tierSlug);
  // TODO - should set error message here
  yield put(replace(D.Utils.Route.getRedeemPath()));
}

export function* nonPaymentBackedSpecialOfferRedemptionSuccess(specialOfferToken) {
  yield put(
    D.PaymentsActions.acquireOffer({
      tier: yield select(SpecialOffersSelectors.getSpecialOfferTier),
      discountType: yield select(SpecialOffersSelectors.getSpecialOfferDiscountType),
      paymentProvider: D.PaymentsConstants.PaymentProviders.NONE,
      durationType: yield select(SpecialOffersSelectors.getSpecialOfferDurationType),
      specialOfferCode: specialOfferToken,
      getSpecialOfferType: SpecialOffersConstants.OfferTypes.ACQUIRE_CODE_PRODUCT,
      vendorSku: yield select(SpecialOffersSelectors.getNonPaymentBackedVendorSku),
    }),
  );
  yield call(
    D.PaymentsHelpers.logUpgradePageStatsEvent,
    SpecialOffersConstants.SpecialOfferStatsActions.OFFER_REDEEM_SUCCESS,
  );
}

export function* specialOfferRedemptionFailure() {
  const isListenerLoggedIn = yield select(D.Utils.Listener.isLoggedIn);
  const isAnonymousListener = yield select(D.Utils.Listener.isAnonymousListener);
  if (isListenerLoggedIn && isAnonymousListener) {
    // if anonymous listener gets error, redirect to login, then to redeem page to display error
    yield put(D.Actions.Login.setAfterLoginRedirect(D.Utils.Route.getRedeemPath()));
    yield put(replace(D.Utils.Route.getLoginPath()));
    return;
  }

  const specialOfferStatus = yield select(SpecialOffersSelectors.getSpecialOfferStatus);
  const isDirectBilledUser = yield select(D.PaymentsSelectors.isDirectBilledUser);
  const {
    isFamilyPromoAgeError,
    isIneligibleForOfferOrUpgrade,
    isAttemptingSidegrade,
    shouldRedirectToRedeemComponent,
  } = SpecialOffersUtils.getSpecialOfferErrorData(specialOfferStatus);

  if (isIneligibleForOfferOrUpgrade) {
    const specialOfferTier = yield select(
      SpecialOffersSelectors.getSpecialOfferTrialIneligibleTier,
    );
    const tier = D.PaymentsUtils.getTierSlugFromTier(specialOfferTier);
    if (isDirectBilledUser) {
      yield call(openIneligibleModal, specialOfferTier);
    }
    yield put(SpecialOffersActions.resetSpecialOffer({ shouldResetError: false }));
    const paymentPage = D.Utils.Route.getPaymentPath(tier, null, null);
    yield put(push(paymentPage));
    yield call(
      logIneligiblePaymentWebViewEvent,
      D.PaymentsUtils.getSpecialOfferErrorViewMode(specialOfferStatus),
    );
  } else if (isFamilyPromoAgeError) {
    return yield put(push(D.Utils.Route.getFamilyPlanPaymentPath()));
  } else if (isAttemptingSidegrade) {
    const isMobileDevice = yield select(D.Utils.Capabilities.isMobileDevice);
    const activeTier = yield select(D.PaymentsSelectors.getActiveProductGroupTier);
    const activeDiscountType = yield select(D.PaymentsSelectors.getActiveProductDiscountType);
    const activePlanName = D.PaymentsUtils.getPlanName(activeTier, activeDiscountType);
    const msg = {
      ...D.PaymentsConstants.Messages.planNotAvailable,
      values: { planName: activePlanName },
    };
    if (isMobileDevice) {
      yield put(D.PaymentsActions.setMobileMessage({ message: msg }));
    }
    yield put(push(D.Utils.Route.getSubscriptionPath()));
    if (!isMobileDevice) {
      yield put(D.Actions.Modal.openModalCoachmark(msg));
    }
    yield call(
      D.PaymentsHelpers.logSubscriptionWebViewEvent,
      D.PaymentsUtils.getSpecialOfferErrorViewMode(specialOfferStatus),
    );
  } else if (shouldRedirectToRedeemComponent) {
    const shouldResetSpecialOffer = SpecialOffersUtils.shouldResetSpecialOffer(specialOfferStatus);
    if (shouldResetSpecialOffer) {
      yield put(SpecialOffersActions.resetSpecialOffer());
    }
    yield put(replace(D.Utils.Route.getRedeemPath()));
  }
  yield call(
    D.PaymentsHelpers.logUpgradePageStatsEvent,
    SpecialOffersConstants.SpecialOfferStatsActions.OFFER_REDEEM_FAILED,
  );
}

export function* openIneligibleModal(tier) {
  const tierDisplayName = D.PaymentsUtils.getPlanName(tier);
  const title = <FormattedMessage {...D.PaymentsConstants.Messages.offerNotAvailable} />;
  const body = (
    <FormattedMessage
      {...D.PaymentsConstants.Messages.notEligibleWithMoreOptions}
      values={{ pandoraTier: tierDisplayName }}
    />
  );
  const payload = {
    modalType: 'ModalStandard',
    title,
    body,
    buttons: [<IneligibleOfferModalButton key={0} />],
    hideXRow: true,
  };
  yield put(D.Actions.Modal.openModal(payload));
}

export function* logIneligiblePaymentWebViewEvent(viewMode) {
  const pageView = D.getInDomain('analytics.constants.StatsPageViewNames.PAYMENT');
  yield put(
    D.Actions.UseCases.webViewModeInteraction({
      pageView,
      viewMode,
    }),
  );
  yield put(
    D.PaymentsActions.logUpgradePageEvent({
      action: D.PaymentsConstants.PartnerUpgradeStatsActions.INELIGIBLE_LANDING_VIEW,
      pageView,
      viewMode,
    }),
  );
}
